import { ChevronDown, Wrench } from 'lucide-react';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { ClubLabel } from '../../components/ClubLabel.tsx';
import { raisonDuFocus } from '../../lib/clubFocus.ts';
import { Colonnes, JaugeFiabilite, pct } from '../../components/Stat.tsx';
import type { PuttingStats, Stats, StatsClub } from '../../lib/training.ts';
import type { DistanceUnit } from '../../lib/units.ts';
import { formatDistance, fromMetres, unitLabel } from '../../lib/units.ts';
import { BandeDistances } from './BandeDistances.tsx';
import { GroupementBalles } from './GroupementBalles.tsx';
import { ProgresClub } from './ProgresClub.tsx';
import { RadarDefauts } from './RadarDefauts.tsx';

/** L'écart d'une valeur à la fenêtre précédente, dit d'un signe et d'un mot.
 *
 *  `null` quand il n'y a rien à comparer : une flèche verte sur une première
 *  séance annoncerait un progrès qui n'a pas eu lieu.
 *
 *  Le SEUIL évite le faux mouvement. Un point de centrage ou un mètre de
 *  distance, c'est le bruit d'une poignée de balles — l'afficher ferait clignoter
 *  une tendance à chaque séance et lui retirerait tout crédit. */
function Tendance({ ecart, suffixe, seuil = 1, bon = 'haut' }: {
    ecart: number | null;
    suffixe?: string;
    seuil?: number;
    /** Quel sens est une bonne nouvelle. La distance qui baisse n'est pas une
     *  faute — elle prend le laiton, pas la brique. */
    bon?: 'haut';
}) {
    if (ecart == null || Math.abs(ecart) < seuil) return null;
    const monte = ecart > 0;
    return (
        <span className="ml-1 whitespace-nowrap text-[11px] font-bold tabular-nums"
            style={{ color: monte === (bon === 'haut') ? 'var(--color-primary)' : 'var(--brass-ink)' }}>
            {monte ? '▲' : '▼'}
            {Math.abs(Math.round(ecart * 10) / 10).toLocaleString('fr-FR')}
            {suffixe}
        </span>
    );
}

/* La liste des clubs : où chacun en est, et de quel côté il va.
 *
 * ── Deux valeurs, et leurs deux pentes ──────────────────────────────────────
 *
 * La fiabilité seule ne dit pas tout d'un club : on peut centrer huit balles
 * sur dix et perdre quinze mètres, ou taper plus loin en se déréglant. Les deux
 * chiffres se lisent donc côte à côte, chacun avec son écart à la période
 * précédente — c'est la pente qui informe, pas la valeur. Un fer 7 à 60 % ne
 * veut rien dire ; un fer 7 à 60 % qui montait à 45 % le mois dernier, si.
 *
 * La distance qui BAISSE prend le laiton et non la brique : perdre des mètres
 * n'est pas une faute, c'est une information. La brique est réservée aux balles
 * manquées.
 *
 * ── Ce que le clic ouvre ────────────────────────────────────────────────────
 *
 * Le radar des défauts, et le détail chiffré. Deux cartes montraient
 * auparavant les mêmes clubs — l'une pour la dernière séance, l'autre pour la
 * fiabilité — et il fallait les lire toutes les deux pour se faire une idée
 * d'un club. Une seule liste, et la profondeur au bout du doigt.
 */
export function ListeClubs({ stats, unit }: { stats: Stats; unit: DistanceUnit }) {
    const clubs = stats.clubs;
    const putting = stats.putting;
    const [ouvert, setOuvert] = useState<string | null>(null);

    /* Dans l'ORDRE DU SAC : du plus long au plus court.
     *
     * La liste était classée par taux de réussite, ce qui la faisait changer
     * d'ordre à chaque séance — le fer 7 en tête un jour, quatrième le
     * lendemain. On ne cherche pas un club en se souvenant de son taux : on le
     * cherche à sa place, celle qu'il occupe dans le sac et qu'il occupe déjà
     * dans Mon matériel. Une position connue se vise sans lire.
     *
     * Le tri porte sur la distance AFFICHÉE, celle qui est mesurée, pour que la
     * colonne décroisse sous les yeux. À défaut de mesure, la distance annoncée
     * tient la place du club plutôt que de le reléguer en fin de liste. */
    const lignes = clubs
        .filter((c) => c.shots > 0)
        .map((c) => {
            const taux = (c.center * 100) / c.shots;
            const tauxAvant = c.prevShots > 0 ? (c.prevCenter * 100) / c.prevShots : null;
            const ecartDistance = c.distanceAvgM != null && c.prevDistanceAvgM != null
                ? fromMetres(c.distanceAvgM, unit) - fromMetres(c.prevDistanceAvgM, unit)
                : null;
            return {
                c,
                taux,
                ecartTaux: tauxAvant == null ? null : taux - tauxAvant,
                ecartDistance,
                /** Les ratés : la matière du radar. */
                rates: c.slice + c.hook + c.top + c.fat + c.shank + c.miss,
                /** Ce qui donne sa place au club dans le sac. */
                rang: c.distanceAvgM ?? c.referenceDistanceM ?? null,
            };
        })
        .sort((a, b) => (b.rang ?? -1) - (a.rang ?? -1));

    return (
        <div className="space-y-2">
            {lignes.map(({ c, taux, ecartTaux, ecartDistance, rates }) => {
                const actif = ouvert === c.clubId;
                return (
                    <div key={c.clubId} className="overflow-hidden rounded-field"
                        style={{ backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
                        {/* TROIS lignes, et non une seule.
                            Le nom, la jauge, puis les deux valeurs : tout tenait
                            d'abord sur une ligne, et « Fer 7 » se coupait en deux
                            dès qu'une puce « À travailler » s'invitait à côté.
                            Deux nombres, deux flèches, un nom de club et un
                            chevron ne tiennent pas dans quatre cents pixels — et
                            c'est le nom qui cédait, c'est-à-dire ce qu'on lit en
                            premier. */}
                        <button type="button"
                            onClick={() => setOuvert(actif ? null : c.clubId)}
                            aria-expanded={actif}
                            className="w-full p-2.5 text-left">
                            <span className="flex items-center gap-1.5">
                                <ClubLabel club={c} className="text-sm" />
                                {/* Marqué parce qu'il est faible, ou parce qu'on
                                    ne le connaît pas encore — le mot suit la
                                    raison, sans quoi « À travailler » à côté d'un
                                    100 % se lit comme un bug. */}
                                {(() => {
                                    const r = raisonDuFocus(c.clubId, stats);
                                    return r == null ? null : (
                                        <span className="badge badge-warning badge-sm shrink-0 gap-1 font-semibold">
                                            <Wrench size={11} aria-hidden="true" />
                                            {r === 'faible' ? 'À travailler' : 'À découvrir'}
                                        </span>
                                    );
                                })()}
                                <span className="flex-1" />
                                <ChevronDown size={16} aria-hidden="true"
                                    className={`shrink-0 transition-transform ${actif ? 'rotate-180' : ''}`} />
                            </span>

                            <span className="mt-1.5 flex">
                                <JaugeFiabilite taux={taux} />
                            </span>

                            <span className="mt-1.5 flex items-baseline justify-between gap-3">
                                <span>
                                    <span className="font-score text-base leading-none tabular-nums">
                                        {pct(Math.round(taux * 10) / 10)}
                                    </span>
                                    <Tendance ecart={ecartTaux} suffixe={' pt'} />
                                    <span className="ml-1.5 text-[9px] font-bold uppercase tracking-[0.1em] opacity-55">
                                        fiabilité
                                    </span>
                                </span>
                                <span className="text-right">
                                    <span className="font-score text-base leading-none tabular-nums">
                                        {c.distanceAvgM != null ? formatDistance(c.distanceAvgM, unit) : '—'}
                                    </span>
                                    <Tendance ecart={ecartDistance} />
                                    <span className="ml-1.5 text-[9px] font-bold uppercase tracking-[0.1em] opacity-55">
                                        distance
                                    </span>
                                </span>
                            </span>
                        </button>

                        {actif && <DetailClub c={c} rates={rates} unit={unit} />}
                    </div>
                );
            })}

            {/* Le PUTTER, s'il a servi.
                Il n'a ni fiabilité ni distance — une balle rentrée n'est pas
                « centrée », et deux mètres de green ne se comparent pas à cent
                cinquante de fairway. Ses chiffres sont ailleurs, dans les
                parties de putting, et c'est pourquoi il manquait à cette liste
                tout en figurant parmi les clubs oubliés le jour même où on
                l'avait sorti.

                Il prend donc la même ligne que les autres, avec ses propres
                mesures : la part de trous rentrés en deux putts — la régulation
                du green — et la moyenne de putts par trou. */}
            {putting != null && putting.holes > 0 && (
                <LignePutter putting={putting}
                    ouvert={ouvert === 'putter'}
                    onBasculer={() => setOuvert(ouvert === 'putter' ? null : 'putter')} />
            )}
        </div>
    );
}

function LignePutter({ putting, ouvert, onBasculer }: {
    putting: PuttingStats;
    ouvert: boolean;
    onBasculer: () => void;
}) {
    const part = (putting.regulation * 100) / putting.holes;
    const deuxPutts = putting.holes - putting.onePutt - putting.threePutt;
    const max = Math.max(1, putting.onePutt, deuxPutts, putting.threePutt);

    return (
        <div className="overflow-hidden rounded-field"
            style={{ backgroundColor: 'var(--tuile)', color: 'var(--tuile-encre)' }}>
            <button type="button" onClick={onBasculer} aria-expanded={ouvert}
                className="w-full p-2.5 text-left">
                <span className="flex items-center gap-1.5">
                    <span className="font-score truncate text-base leading-[1.15]">
                        {putting.clubLabel ?? 'Putter'}
                    </span>
                    <span className="text-xs opacity-60">
                        {putting.games} partie{putting.games > 1 ? 's' : ''}
                    </span>
                    <span className="flex-1" />
                    <ChevronDown size={16} aria-hidden="true"
                        className={`shrink-0 transition-transform ${ouvert ? 'rotate-180' : ''}`} />
                </span>

                <span className="mt-1.5 flex">
                    <JaugeFiabilite taux={part} />
                </span>

                <span className="mt-1.5 flex items-baseline justify-between gap-3">
                    <span>
                        <span className="font-score text-base leading-none tabular-nums">
                            {pct(Math.round(part * 10) / 10)}
                        </span>
                        <span className="ml-1.5 text-[9px] font-bold uppercase tracking-[0.1em] opacity-55">
                            en régulation
                        </span>
                    </span>
                    <span className="text-right">
                        <span className="font-score text-base leading-none tabular-nums">
                            {putting.avgPutts != null
                                ? putting.avgPutts.toLocaleString('fr-FR', { minimumFractionDigits: 2 })
                                : '—'}
                        </span>
                        <span className="ml-1.5 text-[9px] font-bold uppercase tracking-[0.1em] opacity-55">
                            putts / trou
                        </span>
                    </span>
                </span>
            </button>

            {ouvert && (
                <div className="border-t px-2.5 pb-3 pt-2"
                    style={{ borderColor: 'color-mix(in oklab, var(--tuile-encre) 20%, transparent)' }}>
                    {/* Les trois issues d'un green, dans l'ordre où elles coûtent :
                        un putt est gagné, deux est le contrat, trois est perdu. La
                        même rampe que la carte de score — vert, sable, brique. */}
                    <p className="mb-1 text-center text-[11px] font-bold uppercase tracking-[0.12em] opacity-60">
                        Comment il rentre
                    </p>
                    <Colonnes hauteur="5rem" colonnes={[
                        { cle: '1', mot: '1 putt', valeur: String(putting.onePutt),
                            part: putting.onePutt / max, ton: 'var(--color-primary)' },
                        { cle: '2', mot: '2 putts', valeur: String(deuxPutts),
                            part: deuxPutts / max, ton: 'var(--tuile-encre)' },
                        { cle: '3', mot: '3 putts et +', valeur: String(putting.threePutt),
                            part: putting.threePutt / max, ton: 'var(--brique-bord)' },
                    ]} />
                    {/* La légende de la figure, et rien d'autre. « Trous joués »
                        et « En deux putts ou moins » redisaient en deux lignes ce
                        que les trois colonnes montrent et ce que le pourcentage
                        de la ligne repliée résume déjà. */}
                    <p className="mt-0.5 text-center text-xs opacity-60">
                        sur <span className="font-score tabular-nums">{putting.holes}</span>
                        {' '}trou{putting.holes > 1 ? 's' : ''}
                    </p>
                </div>
            )}
        </div>
    );
}

/** Un bloc du panneau : un titre court, et la figure qu'il annonce. */
function Bloc({ titre, children }: { titre: string; children: ReactNode }) {
    return (
        <section className="mt-2 first:mt-0">
            <h4 className="mb-0.5 text-center text-[11px] font-bold uppercase tracking-[0.12em] opacity-55">
                {titre}
            </h4>
            {children}
        </section>
    );
}

/* Le détail d'un club : QUATRE figures, et presque pas de mots.
 *
 * ── Les quatre questions d'un club ──────────────────────────────
 *
 * Est-ce que j'en sors ce qu'il promet ? Est-ce que mes balles se groupent ?
 * Est-ce que je progresse ? Et comment je le rate ? Une figure chacune, dans
 * cet ordre — c'est celui où les questions viennent, de la plus grosse à la
 * plus fine.
 *
 * ── Pourquoi des images plutôt que des nombres ──────────────────────
 *
 * Le panneau a d'abord été une grille de quatre cases — « Amplitude
 * 101–150 m », « Vs annoncé −16 m » — puis un tableau de neuf lignes qui
 * expliquait chaque case. Le premier était illisible, le second trop long à
 * lire. Aucune quantité de texte ne remplace une position sur une règle : les
 * chiffres restent, posés sur les figures, là où ils veulent dire quelque
 * chose.
 */
function DetailClub({ c, rates, unit }: {
    c: StatsClub;
    rates: number;
    unit: DistanceUnit;
}) {
    const en = (m: number | null): number | null => (m == null ? null : fromMetres(m, unit));
    const distances = c.distances.map((m) => fromMetres(m, unit));

    return (
        <div className="border-t px-2.5 pb-2.5 pt-1.5"
            style={{ borderColor: 'color-mix(in oklab, var(--tuile-encre) 20%, transparent)' }}>

            <Bloc titre="Distances">
                {c.distanceAvgM == null || c.distanceMinM == null || c.distanceMaxM == null ? (
                    <p className="py-1 text-center text-xs opacity-60">Aucune distance mesurée.</p>
                ) : (
                    <BandeDistances
                        min={en(c.distanceMinM)!} max={en(c.distanceMaxM)!}
                        moyenne={en(c.distanceAvgM)!}
                        annonce={en(c.referenceDistanceM)}
                        unite={unitLabel(unit)} />
                )}
            </Bloc>

            {distances.length >= 3 && c.distanceAvgM != null && (
                <Bloc titre="Groupement">
                    <GroupementBalles distances={distances} moyenne={en(c.distanceAvgM)!}
                        unite={unitLabel(unit)} />
                </Bloc>
            )}

            {c.history.length >= 2 && (
                <Bloc titre="Évolution">
                    <ProgresClub history={c.history} />
                </Bloc>
            )}

            <Bloc titre="Comment il se rate">
                {rates === 0 ? (
                    <p className="py-1 text-center text-xs opacity-60">Aucune balle manquée.</p>
                ) : (
                    <>
                        <RadarDefauts defauts={c} taille={210} />
                        <p className="mt-0.5 text-center text-xs opacity-60">
                            <span className="font-score tabular-nums">{rates}</span>
                            {' '}ratée{rates > 1 ? 's' : ''} sur{' '}
                            <span className="font-score tabular-nums">{c.shots}</span>
                        </p>
                    </>
                )}
            </Bloc>
        </div>
    );
}
